'use strict';

/**
 * Global Tabs Logic
 * Handles switching between tabs site-wide using event delegation.
 * This avoids the overhead of the component dispatcher for simple UI elements.
 */

/**
 * Publish the active tab's horizontal span as px custom properties on its
 * .nav-tabs-wrapper. The divider (.nav-tabs-wrapper::after) uses them as a mask
 * to punch a transparent gap under the active tab, so the wrapper's own
 * background (e.g. a sheet's noise texture) shows through instead of a flat
 * color box — see MMBrandDetailTabs.ts for the same technique applied to the
 * sticky brand-detail tab bar.
 *
 * Offsets are measured from the wrapper's PADDING edge, not its border box: the
 * divider pseudo-element is inset by the wrapper's own horizontal padding
 * (`left: 18px` MM / `8px` MM6 in the header menu, `0` where the wrapper has no
 * padding), and a mask gradient is resolved against the box it paints — so a
 * border-box measurement would push the gap right by exactly that padding.
 * Wrappers with no padding (PDP sheet, brand pages) are unaffected.
 */
function updateTabGap($wrapper) {
    if (!$wrapper || !$wrapper.length) return;
    var wrapper = $wrapper[0];
    var activeBtn = wrapper.querySelector('.js-tab-btn.active');
    if (!activeBtn) return;

    var wrapRect = wrapper.getBoundingClientRect();
    var btnRect = activeBtn.getBoundingClientRect();
    var padLeft = parseFloat(window.getComputedStyle(wrapper).paddingLeft) || 0;
    var origin = wrapRect.left + padLeft;
    var start = Math.max(0, btnRect.left - origin);
    var end = Math.max(0, btnRect.right - origin);

    wrapper.style.setProperty('--mm-tab-gap-start', start + 'px');
    wrapper.style.setProperty('--mm-tab-gap-end', end + 'px');
}

module.exports = function () {
    // Tab widths can change with viewport (label wrapping, locale, layout) — some
    // wrappers (e.g. the PDP sheet) size off a vw-based sheet width that shifts on
    // every pixel of a window resize. A window 'resize' listener reading
    // getBoundingClientRect synchronously can fire on an intermediate frame during a
    // fast/continuous resize and miss the final settled width, leaving the gap
    // indicator a couple px off until another resize corrects it.
    function updateAllTabGaps() {
        $('.nav-tabs-wrapper').each(function () {
            updateTabGap($(this));
        });
    }

    updateAllTabGaps();

    if (typeof ResizeObserver !== 'undefined') {
        // Reacts fast to box-size changes (e.g. the PDP sheet's vw-based width).
        var tabGapObserver = new ResizeObserver(function (entries) {
            entries.forEach(function (entry) {
                updateTabGap($(entry.target));
            });
        });
        $('.nav-tabs-wrapper').each(function () {
            tabGapObserver.observe(this);
        });
    }

    // Safety net: crossing a breakpoint can restyle the tab bar (padding, font-size,
    // sheet presentation switching side-drawer/bottom-sheet) in a way that shifts the
    // active button without changing the wrapper's own box size on the very last
    // settled frame — which ResizeObserver has nothing to react to, leaving the gap
    // indicator a couple px off with no further event to correct it. A trailing
    // recompute once resize activity actually stops always re-measures the true
    // final layout regardless of what did or didn't trigger the observer.
    var resizeSettleTimeout;
    $(window).on('resize', function () {
        clearTimeout(resizeSettleTimeout);
        resizeSettleTimeout = setTimeout(updateAllTabGaps, 200);
    });

    $(document).on('click', '.js-tab-btn', function (e) {
        e.preventDefault();
        var $btn = $(this);
        var targetId = $btn.data('target');
        
        // Find the closest common container holding BOTH the buttons and the
        // contents. Prefer the top wrapper (.mm-tabs / .tabs-container) directly:
        // some markups insert an extra wrapper (e.g. .nav-tabs-container) between
        // .nav-tabs-wrapper and .mm-tabs, so .closest('.nav-tabs-wrapper').parent()
        // would scope too narrow and miss the sibling .js-tab-content.
        var $container = $btn.closest('.mm-tabs, .tabs-container');

        if (!$container.length) {
            $container = $btn.closest('.nav-tabs-wrapper').parent();
        }

        if (!$container.length) {
            $container = $btn.parent().parent(); // Fallback to two levels up
        }

        var $allBtns = $container.find('.js-tab-btn');
        var $allContents = $container.find('.js-tab-content');

        // Update Buttons
        $allBtns.removeClass('active');
        $btn.addClass('active');

        // Update Contents
        $allContents.removeClass('active');
        var $targetContent = $container.find('#' + targetId);
        
        if ($targetContent.length) {
            $targetContent.addClass('active');
        }

        updateTabGap($btn.closest('.nav-tabs-wrapper'));

        // Uncomment only if needed to keep different style during cross navigations over tabs/brands
        // if ($btn.closest('#header-navigation-menu')) {
        //   let currentMenuBrand = $('#header-navigation-menu').attr('data-brand');
        //   if (currentMenuBrand === 'mm') {
        //     $('#header-navigation-menu').attr('data-brand', 'mm6');
        //   } else {
        //     $('#header-navigation-menu').attr('data-brand', 'mm');
        //   }
        // }
        

        // Dispatch a native CustomEvent for compatibility with other components (like MMNavigationMenu)
        var event = new CustomEvent('MMTabs:tabSwitched', {
            detail: { targetId: targetId, element: $btn[0] },
            bubbles: true
        });
        $btn[0].dispatchEvent(event);
    });

    // Listen for MMSheet:open-tab-target to handle automatic tab switching from external triggers
    document.addEventListener('MMSheet:open', function (e) {
        var targetTab = e.detail && e.detail.tabTarget;
        if (targetTab) {
            // Small timeout to ensure the sheet content is ready/visible
            setTimeout(function() {
                var $tabBtn = $('.mm-tabs .js-tab-btn[data-target="' + targetTab + '"]');
                if ($tabBtn.length) {
                    $tabBtn.trigger('click');
                }
            }, 150);
        }
    });
};
